import { useCallback, useEffect, useMemo, useState } from 'react'
import { getStorage, ref, listAll } from 'firebase/storage'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import type { DocNode } from '@wehunt/ui'
import { buildDocTree } from './buildDocTree'
import { listTeamDocuments, type TeamDocumentDto } from './api/teamDocumentsApi'

interface DocumentFileRecord {
  storagePath?: string
  isFolder?: boolean
  uploaderName?: string | null
  updatedAt?: { toDate(): Date }
}

interface StorageItem {
  path: string
  isFolder: boolean
}

/** Recursively enumerate all files and folders under `documents/{groupId}` in Firebase Storage. */
async function listAllStorageItems(groupId: string): Promise<StorageItem[]> {
  const storage = getStorage()
  const results: StorageItem[] = []

  async function recurse(path: string): Promise<void> {
    const result = await listAll(ref(storage, path))
    for (const prefix of result.prefixes) {
      results.push({ path: prefix.fullPath, isFolder: true })
      await recurse(prefix.fullPath)
    }
    for (const item of result.items) {
      // Skip .keep placeholder files used to represent empty folders
      if (!item.fullPath.endsWith('/.keep')) {
        results.push({ path: item.fullPath, isFolder: false })
      }
    }
  }

  await recurse(`documents/${groupId}`)
  return results
}

/**
 * Convert a TeamDocumentDto into the legacy raw-doc shape `buildDocTree` expects.
 * `updatedAt` arrives as an ISO 8601 string from the API; the tree builder calls
 * `.toDate()` on it, so wrap it in a minimal object exposing that method.
 */
function dtoToMetadata(dto: TeamDocumentDto): DocumentFileRecord {
  return {
    storagePath: dto.storagePath,
    isFolder: dto.isFolder,
    uploaderName: dto.uploaderName,
    updatedAt: dto.updatedAt ? { toDate: () => new Date(dto.updatedAt) } : undefined,
  }
}

export function useGroupDocuments(groupId: string): {
  docTree: Record<string, DocNode> | undefined
  refresh: () => void
} {
  const { state } = useAuth()
  const isSignedIn = state.status === 'signed-in'
  const queryClient = useQueryClient()

  const [storageItems, setStorageItems] = useState<StorageItem[] | undefined>(undefined)
  const [refreshCount, setRefreshCount] = useState(0)

  // Metadata source — react-query against the .NET API.
  const { data: docs } = useQuery({
    queryKey: ['team-documents', groupId],
    enabled: isSignedIn && !!groupId,
    queryFn: async () => {
      const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
      return listTeamDocuments(groupId, token)
    },
  })

  // Storage source — Firebase Storage listing. Re-runs on refresh().
  useEffect(() => {
    if (!isSignedIn || !groupId) {
      setStorageItems(undefined)
      return
    }
    let cancelled = false
    setStorageItems(undefined)
    listAllStorageItems(groupId)
      .then((items) => {
        if (!cancelled) setStorageItems(items)
      })
      .catch((err) => {
        console.error('[useGroupDocuments] Storage listing error:', err)
      })
    return () => {
      cancelled = true
    }
  }, [isSignedIn, groupId, refreshCount])

  // Merge storage entries + API metadata into the tree shape buildDocTree expects.
  const docTree = useMemo<Record<string, DocNode> | undefined>(() => {
    if (!storageItems) return undefined
    const metadataMap = new Map<string, DocumentFileRecord>(
      (docs ?? [])
        .map(dtoToMetadata)
        .filter((r) => !!r.storagePath)
        .map((r) => [r.storagePath!, r]),
    )
    const records: DocumentFileRecord[] = storageItems.map(({ path, isFolder }) => {
      const meta = metadataMap.get(path)
      return {
        storagePath: path,
        isFolder,
        uploaderName: meta?.uploaderName ?? null,
        updatedAt: meta?.updatedAt,
      }
    })
    return buildDocTree(groupId, records)
  }, [groupId, storageItems, docs])

  const refresh = useCallback(() => {
    if (groupId) {
      void queryClient.invalidateQueries({ queryKey: ['team-documents', groupId] })
    }
    setRefreshCount((c) => c + 1)
  }, [groupId, queryClient])

  return { docTree, refresh }
}
